/**
 * @file Auth-expired route (`/auth/expired`) — "this link expired."
 * @module @labcraft/lab/ui/pages/auth-expired
 *
 * Shown after a failed `GET /api/auth/verify` (token missing,
 * already consumed, or past its TTL). Asks for the email again and
 * mints a fresh sign-in link.
 */

import { useState, type FormEvent, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { useSigninWithEmailMutation } from '../../hooks/use_signin_with_email.ts'
import styles from './auth.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function AuthExpiredPage(): JSX.Element {
  const signinWithEmail = useSigninWithEmailMutation()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')

  const submitting = signinWithEmail.isPending
  const canSubmit = !submitting && EMAIL_REGEX.test(email.trim())

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!canSubmit) return
    try {
      await signinWithEmail.mutateAsync({ email: email.trim() })
      navigate(
        `/auth/sent?email=${encodeURIComponent(email.trim())}&purpose=signin`,
        { replace: true },
      )
    } catch {
      // Stay on the page; the user can retry.
    }
  }

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Link expired</p>
        <h1 className={styles.title}>That link has run out.</h1>
        <p className={styles.lede}>
          Sign-in links are single-use and time-limited — yours has done its
          job. Drop your email below and we&apos;ll send a fresh one your way.
        </p>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <button className={styles.cta} type="submit" disabled={!canSubmit}>
            {submitting ? 'Sending…' : 'Send a new link →'}
          </button>
        </form>
      </section>
    </main>
  )
}
